import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import Button from "../../componentes/button/button";
import Input from "../../componentes/input/input";
import { CustomTable } from "../../componentes/table/CustomTable";
import { useAuth } from "../../context/authContext";
import { perfisDoUsuario } from "../../components/solicitacoes/Shared/UltimosLancamentosFortes";
import ContaFinanceiraService from "../../services/resource/contaFinanceiraService";
import { formatarDataHora } from "../../utils/solicitacoes/formatters";

const service = new ContaFinanceiraService();

const FORM_VAZIO = { id: null, codigo: "", nome: "", codigoContabil: "", centroCustoCodigo: "", ativo: true };
const ORIGEM_LABEL = { MANUAL: "Manual", FORTES: "Fortes", PLANILHA: "Planilha" };
const FILTRO_SITUACAO = [
    { value: "TODAS", label: "Todas" },
    { value: "ATIVAS", label: "Ativas" },
    { value: "INATIVAS", label: "Inativas" },
];

function mensagemErro(e, padrao) {
    return e?.response?.data?.message || padrao;
}

/** Resumo de sincronização/importação (contagens + listas de erros e ausentes). */
function ResultadoCarga({ titulo, resultado, onFechar }) {
    if (!resultado) return null;
    const numeros = [
        ["Lidas", resultado.lidas],
        ["Criadas", resultado.criadas],
        ["Atualizadas", resultado.atualizadas],
        ["Sem alteração", resultado.semAlteracao],
        ["Ignoradas", resultado.ignoradas],
    ];
    return (
        <div className="bg-card border border-border rounded-lg shadow-card p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-base font-semibold text-foreground">{titulo}</h2>
                <button type="button" onClick={onFechar} className="text-muted-foreground hover:text-foreground" title="Fechar">
                    <i className="fa fa-times" />
                </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                {numeros.map(([label, valor]) => (
                    <div key={label}>
                        <span className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
                        <span className="font-semibold text-foreground">{valor ?? 0}</span>
                    </div>
                ))}
            </div>
            {resultado.erros?.length > 0 && (
                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-destructive mb-1">Linhas descartadas ({resultado.erros.length})</h3>
                    <ul className="text-xs max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-0.5">
                        {resultado.erros.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </div>
            )}
            {resultado.ausentesNaOrigem?.length > 0 && (
                <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">
                        Cadastradas no Mydash, mas não encontradas no Fortes ({resultado.ausentesNaOrigem.length})
                    </h3>
                    <p className="text-xs text-muted-foreground mb-1">Não foram apagadas nem inativadas — revise e inative, se for o caso.</p>
                    <ul className="text-xs max-h-40 overflow-y-auto border border-border rounded-md p-2 space-y-0.5">
                        {resultado.ausentesNaOrigem.map(c => <li key={c}>{c}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

export default function ContaFinanceiraCrud() {
    const { user } = useAuth();
    const podeAlterar = perfisDoUsuario(user).some(p => ["ADMIN", "CONTADOR"].includes(p));

    const [contas, setContas] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [busca, setBusca] = useState("");
    const [situacao, setSituacao] = useState("TODAS");

    const [modalAberto, setModalAberto] = useState(false);
    const [form, setForm] = useState(FORM_VAZIO);
    const [salvando, setSalvando] = useState(false);

    const [sincronizando, setSincronizando] = useState(false);
    const [importacaoAberta, setImportacaoAberta] = useState(false);
    const [arquivo, setArquivo] = useState(null);
    const [importando, setImportando] = useState(false);
    const [resultado, setResultado] = useState(null);

    const carregar = useCallback(() => {
        setCarregando(true);
        service.listar()
            .then(res => setContas(res.data || []))
            .catch(e => toast.error(mensagemErro(e, "Erro ao carregar as contas financeiras.")))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => {
        document.title = "MyDash | Contas Financeiras";
        carregar();
    }, [carregar]);

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return contas.filter(c => {
            if (situacao === "ATIVAS" && !c.ativo) return false;
            if (situacao === "INATIVAS" && c.ativo) return false;
            if (!termo) return true;
            return [c.codigo, c.nome, c.codigoContabil, c.centroCustoCodigo].some(v => (v || "").toLowerCase().includes(termo));
        });
    }, [contas, busca, situacao]);

    function abrirModal(conta) {
        setForm(conta ? {
            id: conta.id, codigo: conta.codigo || "", nome: conta.nome || "",
            codigoContabil: conta.codigoContabil || "", centroCustoCodigo: conta.centroCustoCodigo || "", ativo: conta.ativo !== false,
        } : FORM_VAZIO);
        setModalAberto(true);
    }

    function salvar() {
        if (!form.codigo.trim() || !form.nome.trim()) {
            toast.error("Informe o código e o nome da conta.");
            return;
        }
        setSalvando(true);
        const requisicao = form.id ? service.atualizar(form.id, form) : service.salvar(form);
        requisicao
            .then(() => {
                toast.success(form.id ? "Conta financeira atualizada." : "Conta financeira cadastrada.");
                setModalAberto(false);
                carregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível salvar a conta financeira.")))
            .finally(() => setSalvando(false));
    }

    function excluir(conta) {
        if (!window.confirm(`Excluir a conta ${conta.codigo} - ${conta.nome}? Se ela só deixou de ser usada, prefira inativá-la.`)) return;
        service.excluir(conta.id)
            .then(() => {
                toast.success("Conta financeira excluída.");
                carregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível excluir a conta financeira.")));
    }

    function sincronizar() {
        setSincronizando(true);
        service.sincronizarComFortes()
            .then(res => {
                setResultado({ titulo: "Sincronização com o Fortes", dados: res.data });
                toast.success(`Sincronização concluída: ${res.data.criadas} criada(s), ${res.data.atualizadas} atualizada(s).`);
                carregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível sincronizar com o Fortes.")))
            .finally(() => setSincronizando(false));
    }

    function importar() {
        if (!arquivo) {
            toast.warning("Selecione a planilha.");
            return;
        }
        const formData = new FormData();
        formData.append("file", arquivo);
        setImportando(true);
        service.importar(formData)
            .then(res => {
                setResultado({ titulo: "Importação de planilha", dados: res.data });
                toast.success(`Importação concluída: ${res.data.criadas} criada(s), ${res.data.atualizadas} atualizada(s).`);
                setImportacaoAberta(false);
                setArquivo(null);
                carregar();
            })
            .catch(e => toast.error(mensagemErro(e, "Não foi possível importar a planilha.")))
            .finally(() => setImportando(false));
    }

    const colunas = [
        { key: "codigo", label: "Código" },
        { key: "nome", label: "Nome" },
        { key: "codigoContabil", label: "Conta Contábil" },
        { key: "centroCustoCodigo", label: "Centro de Custo" },
        {
            key: "ativo", label: "Situação", center: true, render: v => (
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${v ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                    {v ? "Ativa" : "Inativa"}
                </span>
            ),
        },
        { key: "origem", label: "Origem", render: v => ORIGEM_LABEL[v] || "-" },
        { key: "dataUltimaSincronizacao", label: "Última sincronização", render: v => (v ? formatarDataHora(v) : "-") },
        ...(podeAlterar ? [{
            key: "acoes", label: "Ações", center: true, render: (_, conta) => (
                <div className="flex justify-center gap-2">
                    <button type="button" title="Editar" onClick={e => { e.stopPropagation(); abrirModal(conta); }}
                        className="text-primary hover:bg-primary/10 rounded-full p-1">
                        <i className="fa fa-pencil" />
                    </button>
                    <button type="button" title="Excluir" onClick={e => { e.stopPropagation(); excluir(conta); }}
                        className="text-destructive hover:bg-destructive/10 rounded-full p-1">
                        <i className="fa fa-trash" />
                    </button>
                </div>
            ),
        }] : []),
    ];

    return (
        <main className="px-4 py-6 flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold text-foreground">Contas Financeiras</h1>
                    <p className="text-sm text-muted-foreground mt-1">Contas de receita/despesa do Fortes (tabela CRD) usadas no Mydash.</p>
                </div>
                {podeAlterar && (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" label="Importar planilha" iconLeft="fa fa-file-excel" onClick={() => setImportacaoAberta(true)} />
                        <Button variant="outline" label="Sincronizar com Fortes" iconLeft="fa fa-rotate" loading={sincronizando}
                            disabled={sincronizando} onClick={sincronizar} />
                        <Button variant="blue" label="Nova conta" iconLeft="fa fa-plus" onClick={() => abrirModal(null)} />
                    </div>
                )}
            </div>

            <ResultadoCarga titulo={resultado?.titulo} resultado={resultado?.dados} onFechar={() => setResultado(null)} />

            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[240px]">
                    <Input label="Pesquisar" value={busca} onChange={e => setBusca(e.target.value)}
                        placeholder="Código, nome, conta contábil ou centro de custo" />
                </div>
                <div className="flex gap-1 rounded-lg border border-border p-1 bg-card">
                    {FILTRO_SITUACAO.map(op => (
                        <button key={op.value} type="button" onClick={() => setSituacao(op.value)}
                            className={`px-3 py-1.5 rounded-md text-sm font-medium ${situacao === op.value ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"}`}>
                            {op.label}
                        </button>
                    ))}
                </div>
                <span className="text-xs text-muted-foreground pb-2">{filtradas.length} de {contas.length} conta(s)</span>
            </div>

            <CustomTable
                data={filtradas}
                columns={colunas}
                loading={carregando}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[12px] border shadow-card rounded-xl"
                exportFilename="contas_financeiras"
                footer={() => null}
            />

            <Dialog
                header={form.id ? "Editar Conta Financeira" : "Nova Conta Financeira"}
                visible={modalAberto}
                onHide={() => setModalAberto(false)}
                blockScroll
                className="w-[95vw] max-w-2xl"
                footer={(
                    <div className="flex justify-end gap-2 pt-3">
                        <Button variant="outline" label="Cancelar" onClick={() => setModalAberto(false)} />
                        <Button variant="blue" label="Salvar" iconLeft="fa fa-check" loading={salvando} disabled={salvando} onClick={salvar} />
                    </div>
                )}
            >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                    <Input label="Código" required value={form.codigo} onChange={e => setForm(f => ({ ...f, codigo: e.target.value }))}
                        placeholder="Código no Fortes" />
                    <div className="sm:col-span-2">
                        <Input label="Nome" required value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
                    </div>
                    <Input label="Conta Contábil" value={form.codigoContabil} onChange={e => setForm(f => ({ ...f, codigoContabil: e.target.value }))} />
                    <Input label="Centro de Custo" value={form.centroCustoCodigo} onChange={e => setForm(f => ({ ...f, centroCustoCodigo: e.target.value }))} />
                    <label className="flex items-center gap-2 text-sm text-foreground self-end pb-2 cursor-pointer select-none">
                        <input type="checkbox" checked={form.ativo} onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))} />
                        Conta ativa
                    </label>
                </div>
            </Dialog>

            <Dialog
                header="Importar planilha de Contas Financeiras"
                visible={importacaoAberta}
                onHide={() => setImportacaoAberta(false)}
                blockScroll
                className="w-[95vw] max-w-xl"
                footer={(
                    <div className="flex justify-end gap-2 pt-3">
                        <Button variant="outline" label="Cancelar" onClick={() => setImportacaoAberta(false)} />
                        <Button variant="blue" label="Importar" iconLeft="fa fa-upload" loading={importando}
                            disabled={importando || !arquivo} onClick={importar} />
                    </div>
                )}
            >
                <div className="flex flex-col gap-3 text-sm">
                    <p className="text-muted-foreground">
                        A primeira aba precisa de um cabeçalho com as colunas <strong>Código</strong> e <strong>Nome</strong> (ou Descrição).
                        Opcionais: <strong>Conta Contábil</strong>, <strong>Centro de Custo</strong> e <strong>Ativo</strong> (Sim/Não).
                    </p>
                    <p className="text-muted-foreground">
                        Contas novas são cadastradas e as existentes (mesmo código) são atualizadas. Nada é apagado. Linhas inválidas
                        são descartadas e listadas no resultado.
                    </p>
                    <input type="file" accept=".xlsx,.xls,.xlsm" onChange={e => setArquivo(e.target.files?.[0] || null)}
                        className="border border-input rounded-md w-full px-3 py-2 text-sm" />
                </div>
            </Dialog>
        </main>
    );
}
